import type { AgentProgressEvent, AskDashboardContext, AskHistoryTurn, AskResponse } from '../components/OpsCommand/types';
import { localeHeaders } from '../i18n';
import i18n from '../i18n';
import { ApiError, notifyUnauthorized } from './api';
import { buildClientConfigPayload } from './clientConfig';
import { getRequestViewContext } from './requestContext';

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

export type StreamAskArgs = {
  restaurantId: string;
  question: string;
  regionFilter: string | null;
  source: string[] | null;
  shift?: string[] | null;
  /** The status filter of the screen asked from (audit B6); Phase C applies it. */
  status?: string[] | null;
  history: AskHistoryTurn[];
  dashboardContext?: AskDashboardContext | null;
  /** Notebook / Ask session id: lets later turns reuse fetched tables (docs/17). */
  conversationId?: string | null;
  /** The conversation's view context; the screen's on display when not given. */
  viewContext?: unknown;
  /** `update` records viewContext as the conversation's context from now on. */
  contextMode?: 'pin' | 'update';
};

/** Random id for a new agent conversation (server accepts [A-Za-z0-9_-]{8,64}). */
export function newConversationId(): string {
  const random =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID().replace(/-/g, '')
      : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
  return `c_${random}`;
}

export type StreamAskOptions = {
  onEvent: (event: AgentProgressEvent) => void;
  signal?: AbortSignal;
};

function dispatchSseEvent(
  data: string,
  onEvent: (event: AgentProgressEvent) => void,
): AskResponse | null {
  if (!data) {
    return null;
  }
  let parsed: AgentProgressEvent;
  try {
    parsed = JSON.parse(data) as AgentProgressEvent;
  } catch {
    return null;
  }
  onEvent(parsed);
  if (parsed.type === 'done') {
    return parsed.response;
  }
  if (parsed.type === 'error') {
    throw new ApiError(parsed.message);
  }
  return null;
}

/** Incrementally parse SSE frames from a fetch response body. */
export async function consumeSseStream(
  body: ReadableStream<Uint8Array>,
  onEvent: (event: AgentProgressEvent) => void,
  signal?: AbortSignal,
): Promise<AskResponse> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let result: AskResponse | null = null;

  const processBlock = (block: string) => {
    if (!block.trim() || block.startsWith(':')) {
      return;
    }
    let eventType = 'message';
    const dataLines: string[] = [];
    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) {
        eventType = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trimStart());
      }
    }
    const data = dataLines.join('\n');
    if (eventType === 'error' && data && !data.startsWith('{')) {
      onEvent({ type: 'error', message: data });
      throw new ApiError(data);
    }
    const done = dispatchSseEvent(data, onEvent);
    if (done) {
      result = done;
    }
  };

  try {
    while (true) {
      if (signal?.aborted) {
        throw new ApiError(i18n.t('errors:requestAbortedDot'));
      }
      let chunk: ReadableStreamReadResult<Uint8Array>;
      try {
        chunk = await reader.read();
      } catch (readErr) {
        if (signal?.aborted || (readErr instanceof Error && /aborted/i.test(readErr.message))) {
          throw new ApiError(i18n.t('errors:requestAbortedDot'));
        }
        throw readErr;
      }
      const { done, value } = chunk;
      if (done) {
        break;
      }
      buffer += decoder.decode(value, { stream: true });
      let splitAt = buffer.indexOf('\n\n');
      while (splitAt >= 0) {
        const block = buffer.slice(0, splitAt);
        buffer = buffer.slice(splitAt + 2);
        processBlock(block);
        splitAt = buffer.indexOf('\n\n');
      }
    }
    if (buffer.trim()) {
      processBlock(buffer);
    }
  } finally {
    reader.releaseLock();
  }

  if (!result) {
    throw new ApiError(i18n.t('errors:streamEnded'));
  }
  return result;
}

function viewContextField(given: unknown): { viewContext?: unknown } {
  const viewContext = given ?? getRequestViewContext();
  return viewContext ? { viewContext } : {};
}

export async function streamAsk(
  args: StreamAskArgs,
  options: StreamAskOptions,
): Promise<AskResponse> {
  const res = await fetch(`${API_BASE}/api/ask/stream`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...localeHeaders(),
    },
    body: JSON.stringify({
      restaurantId: args.restaurantId,
      question: args.question,
      regionFilter: args.regionFilter,
      source: args.source,
      shift: args.shift ?? null,
      status: args.status ?? null,
      history: args.history,
      dashboardContext: args.dashboardContext ?? null,
      conversationId: args.conversationId ?? null,
      ...viewContextField(args.viewContext),
      ...(args.contextMode === 'update' ? { contextMode: 'update' } : {}),
      clientConfig: buildClientConfigPayload(args.restaurantId),
    }),
    signal: options.signal,
  });

  if (res.status === 401) {
    notifyUnauthorized();
    throw new ApiError(i18n.t('errors:sessionExpired'), 'unauthenticated', 401);
  }

  if (!res.ok) {
    let message = i18n.t('errors:requestFailed', { status: res.status });
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) {
        message = body.error;
      }
    } catch {
      // Non-JSON error body - keep status message.
    }
    throw new ApiError(message);
  }

  if (!res.body) {
    throw new ApiError(i18n.t('errors:streamNoBody'));
  }

  return consumeSseStream(res.body, options.onEvent, options.signal);
}
